import { useEffect, useRef, useState, useCallback } from "react";
import type { RefObject, FC } from "react";

import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch, useSelector } from "react-redux";

import { Box } from "@similarweb/ui-components/foundations";

import { useUnmount } from "app/custom-hooks/useUnmount";
import { translate } from "app/filters/i18nFilter";
import { AI_AGENT_MEETING } from "app/pages/sales-intelligence/constants/routes";
import { AIAgentChatControls } from "app/pages/sales-intelligence/modules/ai-agent/components/AIAgentChatControls";
import { MeetingAgentMessageError } from "app/pages/sales-intelligence/modules/ai-agent/components/MeetingAgentMessageError/MeetingAgentMessageError";
import { useChatScrolling } from "app/pages/sales-intelligence/modules/ai-agent/hooks/useChatScrolling";
import { useCompanyName } from "app/pages/sales-intelligence/modules/ai-agent/hooks/useCompanyName";
import { useIsFocusedState } from "app/pages/sales-intelligence/modules/ai-agent/hooks/useIsFocusedState";
import { aiAgentQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/queries/queryKeyFactory";
import useChatQuery from "app/pages/sales-intelligence/modules/ai-agent/queries/useChatQuery";
import useFeedbackMutation from "app/pages/sales-intelligence/modules/ai-agent/queries/useFeedbackMutation";
import { useGetThreadById } from "app/pages/sales-intelligence/modules/ai-agent/queries/useGetThreadById";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { setChatText, clearChatText, selectChatText } from "app/pages/sales-intelligence/modules/ai-agent/store";
import { openRedirectModal } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/download-pdf/store/action-creators";
import type {
    FeedbackReaction,
    ThreadMessage,
    ThreadMessageDto,
} from "app/pages/sales-intelligence/modules/ai-agent/types/thread";
import { AI_AGENT_PRICE_PER_RECORD } from "app/pages/sales-intelligence/modules/quota/constants";
import { LowQuotaBannerContainer } from "app/pages/sales-intelligence/modules/quota/containers/LowQuotaBannerContainer/LowQuotaBannerContainer";
import { useQuotaGuard } from "app/pages/sales-intelligence/modules/quota/hooks/useQuotaGuard";
import AIAgentChatFooter from "app/pages/sales-intelligence/pages/ai-agent/components/AIAgentChatFooter/AIAgentChatFooter";
import AIAgentMessagesList from "app/pages/sales-intelligence/pages/ai-agent/components/AIAgentMessagesList/AIAgentMessagesList";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import type { ResponseError } from "app/services/fetchService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const MESSAGE_SCROLL_CONTAINER_BOTTOM_OFFSET = 166;

type Props = {
    containerRef: RefObject<HTMLDivElement | null>;
};

const AIAgentChat: FC<Props> = ({ containerRef }) => {
    const dispatch = useDispatch();

    const {
        params: { id: threadId, domain },
    } = useCurrentStateAndParams<{ domain: string; id?: string }>();

    const text = useSelector(selectChatText);

    const onTextChange = useCallback((value?: string) => {
        dispatch(setChatText(value ?? ""));
    }, []);

    const clearText = useCallback(() => {
        dispatch(clearChatText());
    }, []);

    const [activeQuestion, setActiveQuestion] = useState("");
    const [questionId, setQuestionCount] = useState(0);
    const [messages, setMessages] = useState<ThreadMessage[]>([]);

    const lastUserMessageRef = useRef<HTMLDivElement>(null);
    const lastAiMessageRef = useRef<HTMLDivElement>(null);

    const bottomRef = useRef<HTMLDivElement>(null);

    const { scrollToBottom, scrollChatWhenAiResponse, scrollUserMessageToTop } = useChatScrolling({
        containerRef,
        lastAiMessageRef,
        lastUserMessageRef,
        bottomRef,
    });

    const queryClient = useQueryClient();
    const isFetchingThreadGeneration = useIsFetching({ queryKey: aiAgentQueryKeys.generateThread(), exact: true });
    const isInitialThreadGenerationInFlight = isFetchingThreadGeneration > 0;

    const companyName = useCompanyName();

    const {
        data: historyData,
        refetch,
        isLoading: isHistoryDataLoading,
        error: threadError,
    } = useGetThreadById({
        threadId,
        enabled: Boolean(threadId) && !isInitialThreadGenerationInFlight,
    });

    useEffect(() => {
        if ((threadError as ResponseError)?.status === 403) {
            dispatch(openRedirectModal(domain));
            void proNavigator.go(AI_AGENT_MEETING, { domain, id: undefined }, { inherit: false });
        }
    }, [threadError, domain]);

    const handleStreamingComplete = () => {
        void refetch();
    };

    const {
        data,
        stop,
        messageId,
        isError: isChatError,
        refetch: retryChat,
    } = useChatQuery({
        message: activeQuestion,
        threadId,
        questionId,
        onComplete: handleStreamingComplete,
        fetcher: salesApiService.aiAgent.meeting.postAIAgentMessage,
        queryOptions: { throwOnError: false },
    });

    const chatFetchingCount = useIsFetching({ queryKey: aiAgentQueryKeys.chat(questionId.toString()), exact: true });
    const isLoading = chatFetchingCount > 0;

    const { isBlocked } = useQuotaGuard(AI_AGENT_PRICE_PER_RECORD);

    const { mutate } = useFeedbackMutation({ isEmail: false });

    const [isFocused] = useIsFocusedState(!isLoading);

    useEffect(() => {
        if (!historyData?.messages) {
            return;
        }

        setMessages(
            historyData.messages.map((msg: ThreadMessageDto) => {
                if (msg.type === "ai") {
                    return {
                        type: "ai",
                        content: msg.content,
                        id: msg.id ?? "",
                        reaction: msg.isLiked !== undefined ? (msg.isLiked ? "like" : "dislike") : undefined,
                    };
                }

                return {
                    type: "human",
                    content: msg.content,
                };
            }),
        );

        const timeoutId = setTimeout(() => scrollToBottom(), 0);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [historyData]);

    useEffect(() => {
        if (data && messageId) {
            setMessages((prevMessages) => {
                if (activeQuestion) {
                    return [...prevMessages, { type: "ai", content: data, id: messageId }];
                }

                return prevMessages.map((msg, idx) =>
                    idx === prevMessages.length - 1 ? { type: "ai", content: data, id: messageId } : msg,
                );
            });
            setActiveQuestion("");
            requestAnimationFrame(() => scrollChatWhenAiResponse());
        }
    }, [data]);

    useUnmount(() => clearText());

    const handleSubmit = () => {
        if (isFetchingThreadGeneration) {
            AIAgentTrackingService.meeting.trackPauseAgent();
            void queryClient.cancelQueries({ queryKey: aiAgentQueryKeys.generateThread(), exact: true });
            return;
        }

        if (isLoading) {
            AIAgentTrackingService.meeting.trackPauseAgent();
            void stop();
            return;
        }

        const message = text.trim();
        if (!message) {
            return;
        }

        setMessages((prev) => [...prev, { type: "human", content: message }]);
        setActiveQuestion(text);
        setQuestionCount((c) => c + 1);
        clearText();
        AIAgentTrackingService.meeting.trackSendMessage(message);

        requestAnimationFrame(() => {
            scrollUserMessageToTop(MESSAGE_SCROLL_CONTAINER_BOTTOM_OFFSET);
        });
    };

    const handleSubmitFeedback = (id: string, reaction: FeedbackReaction) => {
        if (!threadId) {
            return;
        }

        const alreadyReacted = messages.find((msg) => msg.type === "ai" && msg.id === id && msg.reaction === reaction);
        if (alreadyReacted) {
            return;
        }

        AIAgentTrackingService.meeting.trackSubmitFeedback(companyName, reaction);

        setMessages((prev) => prev.map((msg) => (msg.type === "ai" && msg.id === id ? { ...msg, reaction } : msg)));
        mutate({
            threadId,
            messageId: id,
            liked: reaction === "like",
        });
    };

    const trackCopyClick = () => {
        AIAgentTrackingService.meeting.trackCopyBrief(companyName ?? domain);
    };

    const handleRetryChat = useCallback(() => {
        void retryChat();
    }, [retryChat]);

    const isLastMessageHuman = messages.length > 0 ? messages[messages.length - 1].type === "human" : false;

    if (!threadId) {
        return null;
    }

    return (
        <>
            <AIAgentMessagesList
                messages={messages}
                isLoading={isLoading || isHistoryDataLoading}
                lastUserMessageRef={lastUserMessageRef}
                lastAiMessageRef={lastAiMessageRef}
                onFeedback={handleSubmitFeedback}
                onCopy={trackCopyClick}
                renderLoaderCondition={Boolean(isLoading && isLastMessageHuman)}
                isStreamingLoaderVisible={Boolean(isLoading && !isLastMessageHuman)}
            />
            {isChatError ? <MeetingAgentMessageError onRetry={handleRetryChat} /> : null}
            <Box
                margin="-48px 0 0 0"
                width="100%"
            >
                <LowQuotaBannerContainer />
            </Box>
            <div
                ref={bottomRef}
                id="meeting-agent-bottom"
            />
            <AIAgentChatFooter>
                <AIAgentChatControls
                    isInputLocked={isLoading}
                    isSubmitLocked={isLoading || isFetchingThreadGeneration > 0}
                    isDisabled={isBlocked}
                    isFocused={isFocused}
                    text={text}
                    placeholder={translate("si.pages.ai_agent.input.placeholder")}
                    onSubmit={handleSubmit}
                    onChange={onTextChange}
                />
            </AIAgentChatFooter>
        </>
    );
};

export default AIAgentChat;
